import { supabase } from '../lib/supabase';
import { CACHE_TTL, getCachedData, setCachedData } from './cacheService';

export interface UserBet {
  id: string;
  user_id: string;
  runner_id: number | null;
  race_id: string | null;
  bookmaker: string;
  stake: number;
  odds: number;
  potential_profit: number;
  status: 'pending' | 'won' | 'lost' | 'unmatched' | 'void';
  pnl: number | null;
  placed_at: string;
  settled_at: string | null;
  notes: string | null;
  betfair_bet_id: string | null;
  placed_by_ai: boolean;
}

export interface CreateBetData {
  user_id: string;
  runner_id: number | null;
  bookmaker: string;
  stake: number;
  odds: number;
  notes?: string | null;
  placed_by_ai?: boolean;
}

/**
 * Create a new bet in the database
 */
export async function createBet(betData: CreateBetData): Promise<UserBet> {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .insert({
        user_id: betData.user_id,
        runner_id: betData.runner_id,
        bookmaker: betData.bookmaker,
        stake: betData.stake,
        odds: betData.odds,
        notes: betData.notes || null,
        placed_by_ai: betData.placed_by_ai || false,
        status: 'pending',
        placed_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating bet:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to create bet:', error);
    throw error;
  }
}

/**
 * For Betfair manual placement: insert a new row unless there is a failed FOK row
 * (unmatched, no Betfair bet id) for the same runner — then atomically reset that row
 * so retries do not violate user_id+runner_id uniqueness.
 */
async function tryReuseUnmatchedBetfairRow(
  betId: string,
  betData: CreateBetData
): Promise<UserBet | null> {
  const { data, error } = await supabase
    .from('user_bets')
    .update({
      bookmaker: betData.bookmaker,
      stake: betData.stake,
      odds: betData.odds,
      notes: betData.notes ?? null,
      placed_by_ai: betData.placed_by_ai ?? false,
      status: 'pending',
      placed_at: new Date().toISOString(),
    })
    .eq('id', betId)
    .eq('status', 'unmatched')
    .is('betfair_bet_id', null)
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error reusing unmatched Betfair bet row:', error);
    throw error;
  }
  return data ?? null;
}

export async function createOrReuseUnmatchedBetfairBet(
  betData: CreateBetData
): Promise<UserBet> {
  if (betData.runner_id == null) {
    return createBet(betData);
  }

  const existing = await fetchBetByRunner(betData.user_id, betData.runner_id);

  if (
    existing &&
    existing.status === 'unmatched' &&
    (existing.betfair_bet_id === null || existing.betfair_bet_id === '')
  ) {
    let updated = await tryReuseUnmatchedBetfairRow(existing.id, betData);
    if (updated) {
      return updated;
    }

    const again = await fetchBetByRunner(betData.user_id, betData.runner_id);
    if (
      again &&
      again.status === 'unmatched' &&
      (again.betfair_bet_id === null || again.betfair_bet_id === '')
    ) {
      updated = await tryReuseUnmatchedBetfairRow(again.id, betData);
      if (updated) {
        return updated;
      }
    }

    if (
      again?.status === 'pending' &&
      (again.betfair_bet_id === null || again.betfair_bet_id === '')
    ) {
      throw new Error(
        'A bet placement for this runner is already in progress. Wait a few seconds and try again.'
      );
    }
  }

  return createBet(betData);
}

/**
 * Fetch user's bets from database
 */
export async function fetchUserBets(userId: string): Promise<UserBet[]> {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .order('placed_at', { ascending: false });

    if (error) {
      console.error('Error fetching bets:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch bets:', error);
    throw error;
  }
}

export interface OpenBetWithRace extends UserBet {
  runner?: {
    runner_id: number;
    horse_name: string;
    race_id: string;
  };
  race?: {
    race_id: string;
    course: string;
    race_time: string;
    race_date: string;
    race_class: string;
    race_type: string;
    distance: string;
    going: string;
    ai_selection: string;
  };
  raceRunners?: Array<{
    horse_name: string;
    bet365_decimal_odds?: string | null;
  }>;
}

/** UTC midnight → next day midnight for filtering `placed_at` (timestamps stored in UTC). */
export function getUtcTodayPlacedAtRange(): { startIso: string; endIso: string } {
  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 1);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/** P/L for settled-style rows; matches logic used on Performance page. */
export function calculateBetPl(bet: Pick<UserBet, 'status' | 'pnl' | 'stake' | 'odds' | 'potential_profit'>): number {
  if (bet.pnl !== null && bet.pnl !== undefined) {
    return Number(bet.pnl);
  }
  switch (bet.status) {
    case 'won':
      if (bet.potential_profit !== null && bet.potential_profit !== undefined) {
        return Number(bet.potential_profit);
      }
      return Number(bet.stake) * Number(bet.odds) - Number(bet.stake);
    case 'lost':
      return -Number(bet.stake);
    case 'void':
      return 0;
    default:
      return 0;
  }
}

/** Cash returned to customer (null when not applicable, e.g. pending / unmatched). */
export function calculateBetReturn(bet: Pick<UserBet, 'status' | 'stake' | 'odds'>): number | null {
  switch (bet.status) {
    case 'won':
      return Number(bet.stake) * Number(bet.odds);
    case 'lost':
      return 0;
    case 'void':
      return Number(bet.stake);
    default:
      return null;
  }
}

export async function hydrateBetsWithRunnerAndRace(bets: UserBet[]): Promise<OpenBetWithRace[]> {
  if (!bets.length) {
    return [];
  }

  const runnerIds = bets.map(bet => bet.runner_id).filter((id): id is number => id !== null);
  if (runnerIds.length === 0) {
    return bets.map(bet => ({ ...bet }));
  }

  const { data: runners, error: runnersError } = await supabase
    .from('runners')
    .select('runner_id, horse_name, race_id')
    .in('runner_id', runnerIds);

  if (runnersError) {
    console.error('Error fetching runners:', runnersError);
  }

  const raceIds = runners ? Array.from(new Set(runners.map(r => r.race_id))) : [];
  let races: Array<{
    race_id: string;
    course: string;
    race_time: string;
    race_date: string;
    race_class: string;
    race_type: string;
    distance: string;
    going: string;
    ai_selection: string;
  }> = [];

  let raceRunnersByRaceId: Map<
    string,
    Array<{ horse_name: string; bet365_decimal_odds?: string | null }>
  > = new Map();

  if (raceIds.length > 0) {
    const { data: racesData, error: racesError } = await supabase
      .from('races')
      .select('race_id, course, race_time, race_date, race_class, race_type, distance, going, ai_selection')
      .in('race_id', raceIds);

    if (racesError) {
      console.error('Error fetching races:', racesError);
    } else {
      races = (racesData || []) as typeof races;
    }

    const { data: allRaceRunners, error: allRunnersError } = await supabase
      .from('runners')
      .select('race_id, horse_name, bet365_decimal_odds')
      .in('race_id', raceIds);

    if (allRunnersError) {
      console.error('Error fetching race runners for favourite status:', allRunnersError);
    } else if (allRaceRunners) {
      raceRunnersByRaceId = allRaceRunners.reduce((map, r) => {
        const list = map.get(r.race_id) ?? [];
        list.push({ horse_name: r.horse_name, bet365_decimal_odds: r.bet365_decimal_odds });
        map.set(r.race_id, list);
        return map;
      }, new Map<string, Array<{ horse_name: string; bet365_decimal_odds?: string | null }>>());
    }
  }

  return bets.map(bet => {
    const runner = runners?.find(r => r.runner_id === bet.runner_id);
    const race = runner ? races.find(r => r.race_id === runner.race_id) : undefined;

    return {
      ...bet,
      runner: runner
        ? {
            runner_id: runner.runner_id,
            horse_name: runner.horse_name,
            race_id: runner.race_id
          }
        : undefined,
      race: race
        ? {
            race_id: race.race_id,
            course: race.course,
            race_time: race.race_time,
            race_date: race.race_date,
            race_class: race.race_class,
            race_type: race.race_type,
            distance: race.distance,
            going: race.going,
            ai_selection: race.ai_selection
          }
        : undefined,
      raceRunners: runner ? raceRunnersByRaceId.get(runner.race_id) : undefined
    };
  });
}

/**
 * All bets placed today (UTC day on `placed_at`) with runner and race data.
 */
export async function fetchTodaysBetsWithRace(userId: string): Promise<OpenBetWithRace[]> {
  try {
    const { startIso, endIso } = getUtcTodayPlacedAtRange();

    const { data: bets, error: betsError } = await supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .gte('placed_at', startIso)
      .lt('placed_at', endIso)
      .order('placed_at', { ascending: false });

    if (betsError) {
      console.error('Error fetching today bets:', betsError);
      throw betsError;
    }

    if (!bets || bets.length === 0) {
      return [];
    }

    return hydrateBetsWithRunnerAndRace(bets);
  } catch (error) {
    console.error('Failed to fetch today bets:', error);
    throw error;
  }
}

/**
 * Fetch user's open bets (pending status) with race and runner data
 */
export async function fetchOpenBets(userId: string): Promise<OpenBetWithRace[]> {
  try {
    const { data: bets, error: betsError } = await supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'pending')
      .order('placed_at', { ascending: false });

    if (betsError) {
      console.error('Error fetching open bets:', betsError);
      throw betsError;
    }

    if (!bets || bets.length === 0) {
      return [];
    }

    return hydrateBetsWithRunnerAndRace(bets);
  } catch (error) {
    console.error('Failed to fetch open bets:', error);
    throw error;
  }
}

/**
 * Fetch a bet by user_id and runner_id
 */
export async function fetchBetByRunner(userId: string, runnerId: number): Promise<UserBet | null> {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .eq('runner_id', runnerId)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully

    if (error) {
      // If no bet exists or RLS blocks it, return null (not an error)
      if (error.code === 'PGRST116' || error.code === 'PGRST301') {
        return null;
      }
      console.error('Error fetching bet:', error);
      throw error;
    }

    return data;
  } catch (error: any) {
    // Handle 406 Not Acceptable (RLS issue) or other errors gracefully
    if (error?.code === 'PGRST301' || error?.code === 'PGRST116') {
      return null;
    }
    // Check if it's an HTTP error with status 406
    if (error && typeof error === 'object' && 'status' in error && (error as any).status === 406) {
      return null;
    }
    console.error('Failed to fetch bet:', error);
    throw error;
  }
}

/**
 * Update an existing bet
 */
export async function updateBet(
  betId: string,
  updateData: {
    bookmaker?: string;
    stake?: number;
    odds?: number;
    notes?: string | null;
  }
): Promise<UserBet> {
  try {
    const { data, error } = await supabase
      .from('user_bets')
      .update(updateData)
      .eq('id', betId)
      .select()
      .single();

    if (error) {
      console.error('Error updating bet:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to update bet:', error);
    throw error;
  }
}

/**
 * Delete a bet
 */
export async function deleteBet(betId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_bets')
      .delete()
      .eq('id', betId);

    if (error) {
      console.error('Error deleting bet:', error);
      throw error;
    }
  } catch (error) {
    console.error('Failed to delete bet:', error);
    throw error;
  }
}

/**
 * Update bet status
 */
export async function updateBetStatus(
  betId: string,
  status: UserBet['status'],
  pnl?: number | null,
  settledAt?: string | null
): Promise<UserBet> {
  try {
    const updateData: any = {
      status,
    };

    if (pnl !== undefined) {
      updateData.pnl = pnl;
    }

    if (settledAt !== undefined) {
      updateData.settled_at = settledAt;
    }

    const { data, error } = await supabase
      .from('user_bets')
      .update(updateData)
      .eq('id', betId)
      .select()
      .single();

    if (error) {
      console.error('Error updating bet:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to update bet:', error);
    throw error;
  }
}

/**
 * Update bet with Betfair bet ID
 */
export async function updateBetWithBetfairId(
  betId: string,
  betfairBetId: string,
  averagePriceMatched?: number
): Promise<UserBet> {
  try {
    const updateData: any = {
      betfair_bet_id: betfairBetId,
      status: 'pending', // Bet is matched and pending settlement
    };

    // If average price matched differs from requested odds, update odds
    if (averagePriceMatched !== undefined) {
      updateData.odds = averagePriceMatched;
    }

    const { data, error } = await supabase
      .from('user_bets')
      .update(updateData)
      .eq('id', betId)
      .select()
      .single();

    if (error) {
      console.error('Error updating bet with Betfair ID:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to update bet with Betfair ID:', error);
    throw error;
  }
}

export interface SettledBetWithRace extends UserBet {
  runner?: {
    runner_id: number;
    horse_name: string;
    race_id: string;
  };
  race?: {
    race_id: string;
    course: string;
    race_time: string;
    race_date: string;
    race_class: string;
    race_type: string;
    distance: string;
    going: string;
  };
}

/**
 * Fetch user's settled bets (won, lost, void) with race and runner data
 */
export async function fetchSettledBets(userId: string): Promise<SettledBetWithRace[]> {
  try {
    const { data: bets, error: betsError } = await supabase
      .from('user_bets')
      .select('*')
      .eq('user_id', userId)
      .neq('status', 'pending')
      .order('settled_at', { ascending: false });

    if (betsError) {
      console.error('Error fetching settled bets:', betsError);
      throw betsError;
    }

    if (!bets || bets.length === 0) {
      return [];
    }

    const hydrated = await hydrateBetsWithRunnerAndRace(bets as UserBet[]);
    return hydrated as SettledBetWithRace[];
  } catch (error) {
    console.error('Failed to fetch settled bets:', error);
    throw error;
  }
}

/** Daily bucket from get_performance_aggregates (UTC date_key, matches prior JS chart bucketing). */
export interface PerformanceDailyPlRow {
  date_key: string;
  pl: number;
}

export interface PerformanceAggregates {
  today: {
    pl: number;
    roi: number;
    bets: number;
    winRate: number;
  };
  allTime: {
    netPL: number;
    roi: number;
    totalBets: number;
    winRate: number;
    avgOdds: number;
    avgStake: number;
    wins: number;
    losses: number;
  };
  daily: PerformanceDailyPlRow[];
}

function parseAggregatesJson(raw: unknown): PerformanceAggregates {
  const j = raw as Record<string, unknown>;
  const today = j.today as Record<string, unknown>;
  const allTime = j.allTime as Record<string, unknown>;
  const dailyRaw = j.daily;
  const daily: PerformanceDailyPlRow[] = Array.isArray(dailyRaw)
    ? (dailyRaw as Record<string, unknown>[]).map(d => ({
        date_key: String(d.date_key),
        pl: Number(d.pl)
      }))
    : [];

  return {
    today: {
      pl: Number(today?.pl ?? 0),
      roi: Number(today?.roi ?? 0),
      bets: Number(today?.bets ?? 0),
      winRate: Number(today?.winRate ?? 0)
    },
    allTime: {
      netPL: Number(allTime?.netPL ?? 0),
      roi: Number(allTime?.roi ?? 0),
      totalBets: Number(allTime?.totalBets ?? 0),
      winRate: Number(allTime?.winRate ?? 0),
      avgOdds: Number(allTime?.avgOdds ?? 0),
      avgStake: Number(allTime?.avgStake ?? 0),
      wins: Number(allTime?.wins ?? 0),
      losses: Number(allTime?.losses ?? 0)
    },
    daily
  };
}

/**
 * Full-history aggregates for Performance tiles + chart daily series (RPC).
 * Apply database/performance_rpcs.sql in Supabase before use.
 */
export async function fetchPerformanceAggregates(
  clientTimeZone: string,
  userId?: string
): Promise<PerformanceAggregates> {
  const tz = clientTimeZone?.trim() || 'UTC';
  const cacheKey = userId ? `cache_perf_aggregates_${userId}_${tz}` : null;

  if (cacheKey) {
    const cached = getCachedData<PerformanceAggregates>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const { data, error } = await supabase.rpc('get_performance_aggregates', {
    p_client_tz: tz
  });

  if (error) {
    console.error('Error fetching performance aggregates:', error);
    throw error;
  }

  const raw = data == null
    ? {}
    : typeof data === 'string' ? (JSON.parse(data) as unknown) : data;
  const aggregates = parseAggregatesJson(raw);

  if (cacheKey) {
    setCachedData(cacheKey, aggregates, CACHE_TTL.FIVE_MINUTES);
  }
  return aggregates;
}

/** One page of settled bets with server P/L + cumulative (RPC + runner/race hydration). */
export interface SettledBetPageRow extends SettledBetWithRace {
  effectivePl: number;
  cumulativePl: number;
}

function rpcRowToUserBet(row: Record<string, unknown>): UserBet {
  return {
    id: String(row.id),
    user_id: String(row.user_id),
    runner_id: row.runner_id != null ? Number(row.runner_id) : null,
    race_id: row.race_id != null ? String(row.race_id) : null,
    bookmaker: String(row.bookmaker),
    stake: Number(row.stake),
    odds: Number(row.odds),
    potential_profit: Number(row.potential_profit),
    status: row.status as UserBet['status'],
    pnl: row.pnl != null ? Number(row.pnl) : null,
    placed_at: String(row.placed_at),
    settled_at: row.settled_at != null ? String(row.settled_at) : null,
    notes: row.notes != null ? String(row.notes) : null,
    betfair_bet_id: row.betfair_bet_id != null ? String(row.betfair_bet_id) : null,
    placed_by_ai: Boolean(row.placed_by_ai)
  };
}

export async function fetchSettledBetsPage(
  userId: string,
  options: { limit?: number; offset?: number }
): Promise<SettledBetPageRow[]> {
  const limit = Math.min(200, Math.max(1, options.limit ?? 50));
  const offset = Math.max(0, options.offset ?? 0);
  const cacheKey = `cache_settled_bets_${userId}_${limit}_${offset}`;

  const cached = getCachedData<SettledBetPageRow[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const { data, error } = await supabase.rpc('get_settled_bets_page', {
    p_limit: limit,
    p_offset: offset
  });

  if (error) {
    console.error('Error fetching settled bets page:', error);
    throw error;
  }

  const rows = (data || []) as Record<string, unknown>[];
  if (rows.length === 0) {
    setCachedData(cacheKey, [], CACHE_TTL.FIVE_MINUTES);
    return [];
  }

  const bets = rows.map(r => rpcRowToUserBet(r));
  const hydrated = await hydrateBetsWithRunnerAndRace(bets);
  const base = hydrated as SettledBetWithRace[];

  const page: SettledBetPageRow[] = base.map((bet, i) => ({
    ...bet,
    effectivePl: Number(rows[i].effective_pl),
    cumulativePl: Number(rows[i].cumulative_pl)
  }));

  setCachedData(cacheKey, page, CACHE_TTL.FIVE_MINUTES);
  return page;
}

